import { Link } from "react-router-dom";
import { LogoMark } from "../common/BrandVisuals";
import { useLanguage } from "../../context/LanguageProvider";
import { useSiteData } from "../../context/SiteDataProvider";
import { pick, UI_STRINGS } from "../../lib/i18n";
import { Phone, Mail, MapPin } from "lucide-react";

export function Footer() {
    const { lang } = useLanguage();
    const { contactInfo, siteContent } = useSiteData();
    const logoUrl = siteContent?.images?.logo_url || "";

    return (
        <footer
            data-testid="site-footer"
            className="bg-ink-deep border-t border-ink-border text-neutral-300"
        >
            <div className="mx-auto max-w-7xl px-5 lg:px-10 py-16 grid grid-cols-1 md:grid-cols-3 gap-12">
                <div>
                    <LogoMark size="md" src={logoUrl} />
                    <p className="mt-6 text-sm text-neutral-400 leading-relaxed max-w-sm">
                        {pick(
                            {
                                fr: "Cabinet d'avocats à Bobo-Dioulasso, avec une pratique active au Barreau du Québec et au Barreau du Burkina Faso.",
                                en: "Law firm in Bobo-Dioulasso with an active practice at the Barreau du Québec and the Bar of Burkina Faso.",
                            },
                            lang,
                        )}
                    </p>
                    <p className="mt-4 text-xs uppercase tracking-widest text-gold">
                        {pick(UI_STRINGS.misc.dual_bar_short, lang)}
                    </p>
                </div>

                <div>
                    <div className="eyebrow mb-4">
                        {pick(
                            { fr: "Navigation", en: "Navigation" },
                            lang,
                        )}
                    </div>
                    <ul className="space-y-3 text-sm">
                        {Object.entries(UI_STRINGS.nav).map(([key, val]) => (
                            <li key={key}>
                                <Link
                                    to={
                                        key === "home"
                                            ? "/"
                                            : key === "about"
                                              ? "/a-propos"
                                              : key === "practice"
                                                ? "/domaines"
                                                : key === "process"
                                                  ? "/fonctionnement"
                                                  : key === "blog"
                                                    ? "/blog"
                                                    : "/contact"
                                    }
                                    className="text-neutral-300 hover:text-gold transition-colors"
                                    data-testid={`footer-nav-${key}`}
                                >
                                    {pick(val, lang)}
                                </Link>
                            </li>
                        ))}
                        <li>
                            <Link
                                to="/mentions-legales"
                                className="text-neutral-400 hover:text-gold transition-colors"
                                data-testid="footer-nav-legal"
                            >
                                {pick(UI_STRINGS.misc.legal_link, lang)}
                            </Link>
                        </li>
                    </ul>
                </div>

                <div>
                    <div className="eyebrow mb-4">
                        {pick(UI_STRINGS.nav.contact, lang)}
                    </div>
                    <ul className="space-y-3 text-sm">
                        <li className="flex gap-3 items-start text-neutral-300">
                            <MapPin
                                size={16}
                                className="text-gold mt-0.5 shrink-0"
                            />
                            <span data-testid="footer-address">
                                {contactInfo
                                    ? pick(contactInfo.address, lang)
                                    : "Bobo-Dioulasso, Burkina Faso"}
                            </span>
                        </li>
                        {contactInfo?.phones?.slice(0, 2).map((p) => (
                            <li
                                key={p}
                                className="flex gap-3 items-start text-neutral-300"
                            >
                                <Phone size={16} className="text-gold mt-0.5" />
                                <a
                                    href={`tel:${p.replace(/\s+/g, "")}`}
                                    className="hover:text-gold transition-colors"
                                    data-testid={`footer-phone-${p.replace(/[^\d]/g, "")}`}
                                >
                                    {p}
                                </a>
                            </li>
                        ))}
                        {contactInfo?.email_main && (
                            <li className="flex gap-3 items-start text-neutral-300">
                                <Mail size={16} className="text-gold mt-0.5" />
                                <a
                                    href={`mailto:${contactInfo.email_main}`}
                                    className="hover:text-gold transition-colors break-all"
                                    data-testid="footer-email"
                                >
                                    {contactInfo.email_main}
                                </a>
                            </li>
                        )}
                    </ul>
                </div>
            </div>

            <div className="border-t border-ink-border">
                <div className="mx-auto max-w-7xl px-5 lg:px-10 py-6 flex flex-col md:flex-row items-start md:items-center justify-between gap-3">
                    <div className="text-xs text-neutral-500">
                        {pick(UI_STRINGS.misc.copyright, lang)} —{" "}
                        {pick(UI_STRINGS.misc.member_of, lang)}
                    </div>
                    <div
                        className="text-xs text-neutral-500 max-w-2xl md:text-right"
                        data-testid="footer-legal-disclaimer"
                    >
                        {pick(UI_STRINGS.misc.legal_short, lang)}
                    </div>
                </div>
            </div>
        </footer>
    );
}
